import React, {useEffect, useRef} from 'react';
import {MembershipPdfViewerProps} from "../../../model";
import {reportService} from "../../../services/domain/report/service";
import PdfViewer from "../../../components/shared/PdfViewer";

/**
 * 會員報表PDF預覽元件
 * Wrapper around the shared PdfViewer component with membership-specific functionality
 */
const MembershipPdfViewer: React.FC<MembershipPdfViewerProps> = ({
  visible,
  onClose,
  reportType,
  apiParam,
  title
}) => {
  const requestAbort = useRef<AbortController | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    // React StrictMode replays effects in development; only abort a real unmount.
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    return () => { closeTimer.current = setTimeout(() => requestAbort.current?.abort(), 0); };
  }, []);
  /**
   * Load membership PDF data
   */
  const loadPdfData = async () => {
    // Get PDF data based on report type
    let response;
    switch (reportType) {
      case 'MembershipPaper': // 全會員證書
        response = await reportService.getMembershipPaperPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'Membership': // 會員資料
        requestAbort.current?.abort();
        requestAbort.current = new AbortController();
        response = await reportService.getMembershipPdf( {
          "membershipIds": apiParam.membershipIds
        }, requestAbort.current.signal);
        break;
      case 'MemberProducts': // 會員產品列表
        response = await reportService.getMemberProductsPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'MembershipInfo': // 會員基本資料列表
        response = await reportService.getMembershipInfoPdf( {
          "membershipIds": apiParam.membershipIds,
          "language": apiParam.language
        });
        break;
      case 'MembershipNametag': // 會員郵寄名條列表
        response = await reportService.getMembershipNametagPdf({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType,
          "order": "MEMBER_ID",
          "language": apiParam.language
        });
        break;
      case 'MemberCompany': // 會員公司名冊
        response = await reportService.getMemberCompanyPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID",
          "language": apiParam.language
        });
        break;
      case 'MemberFactory': // 會員工廠名冊
        response = await reportService.getMemberFactoryPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID",
          "language": apiParam.language
        });
        break;
      case 'MemberDelegate': // 會員代表名冊
        response = await reportService.getMemberDelegatePdf({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType
        });
        break;
      case 'MemberDelegateNametag': // 會員代表名條
        response = await reportService.getMemberDelegateNametagPdf({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType
        });
        break;
      case 'MembershipWebsiteEmail': // 會員網址、電子郵件列表
        response = await reportService.getMembershipWebsiteEmailPdf({
          "membershipIds": apiParam.membershipIds
        });
        break;
      case 'MembershipJoinDate': // 會員入會日期列表
        response = await reportService.getMembershipJoinDatePdf({
          "membershipIds": apiParam.membershipIds
        });
        break;
      case 'MembershipSuspension': // 停權會員列表
        response = await reportService.getMembershipSuspensionPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'MembershipWithdrawal': // 退會會員列表
        response = await reportService.getMembershipWithdrawalPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'MemberDelegateCard': // 會員代表卡列印
        response = await reportService.getMemberDelegateCardPdf({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType
        });
        break;
    }
    return response;
  };

  /**
   * Export PDF
   */
  const exportPdf = async (filename: string) => {
    // In production, we would handle different report types here
    switch (reportType) {
      case 'MembershipPaper': // 全會員證書
        await reportService.exportMembershipPaperPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'Membership': // 會員資料
        await reportService.exportMembershipPdf( {
          "membershipIds": apiParam.membershipIds
        });
        break;
      case 'MemberProducts': // 會員產品列表
        await reportService.exportMemberProductsPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'MembershipInfo': // 會員基本資料列表
        await reportService.exportMembershipInfoPdf( {
          "membershipIds": apiParam.membershipIds,
          "language": apiParam.language
        });
        break;
      case 'MembershipNametag': // 會員郵寄名條列表
        await reportService.exportMembershipNametagPdf({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType,
          "order": "MEMBER_ID",
          "language": apiParam.language
        });
        break;
      case 'MemberCompany': // 會員公司名冊
        await reportService.exportMemberCompanyPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID",
          "language": apiParam.language
        });
        break;
      case 'MemberFactory': // 會員工廠名冊
        await reportService.exportMemberFactoryPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID",
          "language": apiParam.language
        });
        break;
      case 'MemberDelegate': // 會員代表名冊
        await reportService.exportMemberDelegatePdf({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType
        });
        break;
      case 'MemberDelegateNametag': // 會員代表名條
        await reportService.exportMemberDelegateNametagPdf({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType
        });
        break;
      case 'MembershipWebsiteEmail': // 會員網址、電子郵件列表
        await reportService.exportMembershipWebsiteEmailPdf({
          "membershipIds": apiParam.membershipIds
        });
        break;
      case 'MembershipJoinDate': // 會員入會日期列表
        await reportService.exportMembershipJoinDatePdf({
          "membershipIds": apiParam.membershipIds
        });
        break;
      case 'MembershipSuspension': // 停權會員列表
        await reportService.exportMembershipSuspensionPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'MembershipWithdrawal': // 退會會員列表
        await reportService.exportMembershipWithdrawalPdf({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'MemberDelegateCard': // 會員代表卡列印
        await reportService.exportMemberDelegateCardPdf({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType
        });
        break;
    }
  };

  /**
   * Export Excel
   */
  const exportExcel = async (filename: string) => {
    // In production, we would handle different report types here
    switch (reportType) {
      case 'MembershipPaper': // 全會員證書
        await reportService.exportMembershipPaperExcel({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'Membership': // 會員資料
        await reportService.exportMembershipExcel( {
          "membershipIds": apiParam.membershipIds
        });
        break;
      case 'MemberProducts': // 會員產品列表
        await reportService.exportMemberProductsExcel({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'MembershipInfo': // 會員基本資料列表
        await reportService.exportMembershipInfoExcel( {
          "membershipIds": apiParam.membershipIds,
          "language": apiParam.language
        });
        break;
      case 'MembershipNametag': // 會員郵寄名條列表
        await reportService.exportMembershipNametagExcel({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType,
          "order": "MEMBER_ID",
          "language": apiParam.language
        });
        break;
      case 'MemberCompany': // 會員公司名冊
        await reportService.exportMemberCompanyExcel({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID",
          "language": apiParam.language
        });
        break;
      case 'MemberFactory': // 會員工廠名冊
        await reportService.exportMemberFactoryExcel({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID",
          "language": apiParam.language
        });
        break;
      case 'MemberDelegate': // 會員代表名冊
        await reportService.exportMemberDelegateExcel({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType
        });
        break;
      case 'MemberDelegateNametag': // 會員代表名條
        await reportService.exportMemberDelegateNametagExcel({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType
        });
        break;
      case 'MembershipWebsiteEmail': // 會員網址、電子郵件列表
        await reportService.exportMembershipWebsiteEmailExcel({
          "membershipIds": apiParam.membershipIds
        });
        break;
      case 'MembershipJoinDate': // 會員入會日期列表
        await reportService.exportMembershipJoinDateExcel({
          "membershipIds": apiParam.membershipIds
        });
        break;
      case 'MembershipSuspension': // 停權會員列表
        await reportService.exportMembershipSuspensionExcel({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'MembershipWithdrawal': // 退會會員列表
        await reportService.exportMembershipWithdrawalExcel({
          "membershipIds": apiParam.membershipIds,
          "order": "MEMBER_ID"
        });
        break;
      case 'MemberDelegateCard': // 會員代表卡列印
        await reportService.exportMemberDelegateCardExcel({
          "membershipIds": apiParam.membershipIds,
          "type": apiParam.identityType
        });
        break;
    }
  };

  return (
    <PdfViewer
      visible={visible}
      onClose={onClose}
      title={title}
      loadPdfData={loadPdfData}
      exportPdf={exportPdf}
      exportExcel={exportExcel}
      reuseLoadedPdf={reportType === 'Membership'}
    />
  );
};

export default MembershipPdfViewer;
